import { Alert, Anchor, Button, PasswordInput, Stack, TextInput } from '@mantine/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';

import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { returnTo } from '../auth/returnTo';
import { loginSchema, type LoginValues } from '../auth/schemas';
import { AuthCard } from '../components/AuthCard';
import { ru } from '../i18n/ru';
import { accentText } from '../theme';

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const [failure, setFailure] = useState<string | null>(null);

  // Куда уйти после входа: закрытая страница, с которой гостя увели сюда.
  // Читаем при открытии формы — адрес уже сохранён к этому моменту, — а стираем
  // только после успешного входа, чтобы неверный пароль цель не терял.
  const [destination] = useState(() => returnTo.peek() ?? '/');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // Переход делает этот же <Navigate> — и для уже вошедшего, и сразу после
  // входа. Императивный navigate() в обработчике здесь проигрывал гонку:
  // состояние входа обновляется с обычным приоритетом, а переход роутера идёт
  // в transition, поэтому перерисовка успевала увести на главную.
  if (isAuthenticated) {
    return <Navigate to={destination} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await login(values.email, values.password);
      returnTo.clear();
    } catch (error) {
      setFailure(errorMessage(error, 'Не удалось войти.'));
    }
  });

  return (
    <AuthCard title={ru.auth.loginTitle}>
      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <Stack gap="md">
          {failure !== null && (
            <Alert color="red" title={ru.common.error}>
              {failure}
            </Alert>
          )}

          <TextInput
            label={ru.fields.email}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />

          <PasswordInput
            label={ru.fields.password}
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          <Button type="submit" loading={isSubmitting} mt="xs">
            {ru.auth.loginSubmit}
          </Button>

          <Stack gap={6}>
            <Anchor component={Link} to="/forgot-password" size="sm" c={accentText}>
              {ru.auth.forgotPassword}
            </Anchor>
            <Anchor component={Link} to="/register" size="sm" c={accentText}>
              {ru.auth.noAccount}
            </Anchor>
          </Stack>
        </Stack>
      </form>
    </AuthCard>
  );
}
